import { CalendarClock, HeartHandshake, Pencil, Plus, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { membershipPath } from '@/app/paths';

import { audienceAudit } from '@/components/AudienceDialog';
import { useOverlays } from '@/components/Overlays/context';
import { VisitorDialog } from '@/components/VisitorDialog';
import { Button, buttonVariants } from '@/design-system/ui/Button';
import { audienceMethodLabel, visitorsOf } from '@escala/domain/audience';
import { formatDayMonth } from '@escala/domain/dates';
import type { ScheduleEvent } from '@escala/domain/types';
import { visitorsOfEvent } from '@escala/domain/visitors';
import { useStore } from '@/store/AppStore';

const n = (value: number | null) => (value === null ? '—' : value.toLocaleString('pt-BR'));

/** Um número do registro: rótulo, valor e a explicação curta. */
function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-block border border-line-card px-3.5 py-3">
      <dt className="text-meta font-medium text-ink-muted">{label}</dt>
      <dd data-numeric className="m-0 text-title font-bold tracking-[-0.01em] text-ink">
        {value}
      </dd>
      {hint ? <span className="text-caption text-ink-subtle">{hint}</span> : null}
    </div>
  );
}

/**
 * Painel do evento › Público do culto. Registrado: os totais, o método e quem registrou.
 * Passado sem registro: "Público não registrado" (não é zero) e o botão de registrar.
 * Futuro: o registro abre no dia do culto. Só o Administrador registra; os líderes leem.
 */
export default function AudiencePanel({ event }: { event: ScheduleEvent }) {
  const { permissions, today } = useStore();

  return (
    <div className="flex flex-col gap-4">
      <AudienceFigures event={event} />
      {permissions.manageVisitors && event.date <= today ? <EventVisitors event={event} /> : null}
    </div>
  );
}

/**
 * As fichas de visitantes deste culto (para quem cuida dos visitantes): quantas e o atalho
 * para registrar uma já com o culto. A ficha é à parte da contagem — a contagem não cria
 * ficha, e nem todo visitante deixa a dele.
 */
function EventVisitors({ event }: { event: ScheduleEvent }) {
  const { state } = useStore();
  const [adding, setAdding] = useState(false);
  const count = visitorsOfEvent(state.visitors, event.id).filter((v) => !v.anonymized).length;

  return (
    <section
      aria-label="Fichas de visitantes"
      className="flex flex-wrap items-center gap-2.5 border-t border-line-divider pt-3.5"
    >
      <HeartHandshake aria-hidden className="size-4 shrink-0 stroke-[1.8] text-ink-muted" />
      <span className="min-w-50 flex-1 text-small text-ink-secondary">
        {count === 0
          ? 'Nenhuma ficha de visitante deste evento.'
          : `${count} ${count === 1 ? 'ficha de visitante' : 'fichas de visitantes'} deste evento.`}
      </span>
      <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
        <Plus aria-hidden />
        Adicionar ficha
      </Button>
      <Link to={membershipPath('visitantes')} className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
        Ver fichas
      </Link>
      {adding ? <VisitorDialog eventId={event.id} onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function AudienceFigures({ event }: { event: ScheduleEvent }) {
  const { permissions, today } = useStore();
  const { openAudience } = useOverlays();
  const a = event.audience;
  const canRecord = permissions.recordAudience;

  if (event.date > today) {
    return (
      <div className="flex gap-2.5 rounded-block border border-line-input bg-surface-subtle px-3.5 py-3 text-small text-ink-secondary">
        <CalendarClock aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-ink-muted" />O registro
        do público fica disponível a partir do dia do evento ({formatDayMonth(event.date)}).
      </div>
    );
  }

  if (!a) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-2.5 rounded-block border border-status-partial-line bg-warning-soft px-3.5 py-3 text-small text-warning-deep">
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-status-partial" />
          <span>
            <strong className="font-semibold">Público não registrado.</strong> O evento aparece assim nos
            indicadores da Membresia — não como zero.
          </span>
        </div>
        {canRecord ? (
          <Button className="self-start" onClick={() => openAudience(event.id)}>
            <Plus aria-hidden />
            Registrar público
          </Button>
        ) : (
          <p className="m-0 text-small text-ink-subtle">
            Quem registra o público é a administração da igreja.
          </p>
        )}
      </div>
    );
  }

  const visitors = visitorsOf(a);

  return (
    <div className="flex flex-col gap-3.5">
      <dl className="m-0 grid grid-cols-2 gap-2.5">
        <Figure label="Presencial" value={n(a.total)} hint="Crianças incluídas" />
        <Figure label="Online" value={n(a.online)} hint="Separado do presencial" />
        <Figure
          label="Visitantes"
          value={n(visitors)}
          hint={
            visitors === null
              ? ''
              : `${n(a.firstTime ?? 0)} de primeira vez · ${n(a.recurring ?? 0)} de retorno`
          }
        />
        <Figure label="Membros" value={n(a.members)} />
        <Figure label="Crianças" value={n(a.kids)} hint="Já contadas no presencial" />
        <Figure label="Contagem" value={audienceMethodLabel(a.method)} />
      </dl>
      {a.note ? (
        <p className="m-0 rounded-control bg-surface-muted px-3.5 py-2.5 text-small text-ink-secondary">
          {a.note}
        </p>
      ) : null}
      <p className="m-0 text-meta text-ink-subtle">{audienceAudit(event)}</p>
      {canRecord ? (
        <Button variant="secondary" className="self-start" onClick={() => openAudience(event.id)}>
          <Pencil aria-hidden />
          Editar registro
        </Button>
      ) : null}
    </div>
  );
}
